import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { suppliesService, warehousesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import useLookup from '../../utils/useLookup';
import { STATUS_OPTIONS } from '../../constants/listOptions';
import ConfirmDialog from '../../components/ConfirmDialog';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatBRL, formatQuantity } from '../../lib/utils';

function SuppliesList() {
    usePageTitle('Insumos');
    const navigate = useNavigate();
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canSupplies = can('Supplies');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

    // Carrega dados iniciais

    const list = useTableList({
        storageKey: 'supplies',
        fetcher: (params) => suppliesService.list(params),
    });

    const handleNewSupply = () => {
        navigate('/production/supplies/new');
    };

    const handleEditSupply = (supply) => {
        navigate(`/production/supplies/${supply.id}/edit`);
    };

    const handleDeleteClick = (supply) => {
        setDeleteDialog({ open: true, id: supply.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await suppliesService.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Insumo excluído',
                description: 'O insumo foi excluído com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error deleting supply:', error);
            toastApiError(error, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o insumo' });
        }
    };


    const getStatusBadge = (status) => {
        const statusMap = {
            active: { label: 'Ativo', variant: 'success' },
            inactive: { label: 'Inativo', variant: 'secondary' },
        };
        const config = statusMap[status] || { label: status, variant: 'default' };
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const columns = [
        {
            key: 'code',
            label: 'Código',
            className: 'font-medium',
        },
        {
            key: 'description',
            label: 'Descrição',
        },
        {
            key: 'unit_of_measure',
            label: 'Unidade',
        },
        {
            key: 'stock_quantity',
            label: 'Estoque',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => formatQuantity(value),
        },
        {
            key: 'unit_cost',
            label: 'Custo Unitário',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => formatBRL(value),
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => getStatusBadge(value),
        },
    ];

    const warehouses = useLookup(() => warehousesService.list({ per_page: 1000 }));

    const filters = [
        { key: 'code', label: 'Código', type: 'text' },
        { key: 'description', label: 'Descrição', type: 'text' },
        { key: 'unit', label: 'Unidade', type: 'text' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
        { key: 'default_warehouse_id', label: 'Depósito padrão', type: 'select', options: warehouses.map((w) => ({ value: String(w.id), label: w.name })) },
        { key: 'cost_min', label: 'Custo unitário mínimo', type: 'number' },
        { key: 'cost_max', label: 'Custo unitário máximo', type: 'number' },
    ];

    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title="Insumos"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/supplies' },
                    ]}
                    actions={[
                        ...(canSupplies ? [{
                            label: 'Novo Insumo',
                            icon: 'new',
                            as: Link,
                            to: '/production/supplies/new',
                        }] : []),
                    ]}
                />

                {/* Table */}
                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por código ou descrição..."
                    columns={columns}
                    editTo={canSupplies ? (row) => `/production/supplies/${row.id}/edit` : undefined}
                    onDelete={canSupplies ? handleDeleteClick : undefined}
                    emptyMessage="Nenhum insumo cadastrado"
                />
            </div>


            {/* Confirm Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir Insumo"
                message="Tem certeza que deseja excluir este insumo? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />
        </AppLayout>
    );
}

export default SuppliesList;

